import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/axios';

const C = {
  ink: '#0B0E17', surface: '#1E2235', border: '#2A2E45',
  muted: '#8892A4', gold: '#F0B429', teal: '#00C9B1',
  coral: '#FF6B6B', text: '#E8EDF5', sub: '#C5CDD8',
};

const PASSWORD_RULES = [
  { key: 'length', text: 'At least 8 characters', test: (password) => password.length >= 8 },
  { key: 'upper', text: 'At least 1 uppercase letter', test: (password) => /[A-Z]/.test(password) },
  { key: 'lower', text: 'At least 1 lowercase letter', test: (password) => /[a-z]/.test(password) },
  { key: 'special', text: 'At least 1 special character', test: (password) => /[^A-Za-z\d]/.test(password) },
  { key: 'number', text: 'At least 1 number', test: (password) => /\d/.test(password) },
];

const inputStyle = {
  width: '100%',
  padding: '10px 13px',
  background: 'rgba(255,255,255,0.04)',
  border: `1.5px solid ${C.border}`,
  borderRadius: 10,
  fontSize: '0.875rem',
  color: C.text,
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block',
  fontSize: '0.72rem',
  fontWeight: 700,
  color: C.muted,
  marginBottom: 6,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const passwordChecks = useMemo(
    () => PASSWORD_RULES.map((rule) => ({ ...rule, met: rule.test(password) })),
    [password]
  );
  const passwordValid = passwordChecks.every((rule) => rule.met);
  const passwordsMatch = password === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!token) {
      setError('Reset token is missing from the link.');
      return;
    }

    if (!passwordValid || !passwordsMatch) {
      setError('Please meet the password requirements and confirm your password.');
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post('/auth/reset-password', { token, password });
      setSuccess(data.message || 'Password reset successfully.');
      window.setTimeout(() => navigate('/login'), 1600);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: C.ink,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'Cabinet Grotesk', sans-serif",
      padding: 24,
    }}>
      <div style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 20,
        padding: 40,
        width: '100%',
        maxWidth: 420,
        boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Link to="/">
            <img
              src="/optika-logo.svg"
              alt="OptiKa"
              style={{ height: 40, width: 'auto', marginBottom: 8 }}
            />
          </Link>
          <p style={{ color: C.muted, fontSize: 13, margin: 0 }}>
            Choose a new password for your account
          </p>
        </div>

        {error && (
          <div style={{
            padding: '12px 14px',
            background: 'rgba(255,107,107,0.08)',
            border: '1px solid rgba(255,107,107,0.25)',
            borderRadius: 10,
            color: C.coral,
            fontSize: '0.85rem',
            marginBottom: 16,
          }}>
            {error}
          </div>
        )}
        {success && (
          <div
            style={{
              marginBottom: 16,
              padding: '12px 14px',
              borderRadius: 10,
              background: 'rgba(0,201,177,0.08)',
              border: '1px solid rgba(0,201,177,0.25)',
              color: C.teal,
              fontSize: '0.9rem',
            }}
          >
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>New Password</label>
            <input style={inputStyle} type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} />
            <div style={{ marginTop: 8, padding: '10px 14px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, border: `1px solid ${C.border}` }}>
              <p style={{ fontSize: 11, color: C.muted, fontWeight: 600, marginBottom: 6 }}>
                Password requirements: At least 8 characters, 1 uppercase, 1 lowercase, 1 special character, and 1 number
              </p>
              {passwordChecks.map((rule) => (
                <div key={rule.key} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  <span style={{ fontSize: 12, color: rule.met ? C.teal : C.muted }}>{rule.met ? '✓' : '○'}</span>
                  <span style={{ fontSize: 12, color: rule.met ? C.teal : C.sub }}>{rule.text}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Confirm New Password</label>
            <input style={inputStyle} type="password" placeholder="Re-enter your password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={8} />
            {confirmPassword.length > 0 && !passwordsMatch && (
              <p style={{ marginTop: 8, fontSize: '0.8rem', color: C.coral }}>Passwords do not match.</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading || !passwordValid || !passwordsMatch}
            style={{
              width: '100%',
              justifyContent: 'center',
              padding: 12,
              marginTop: 8,
              background: C.gold,
              color: C.ink,
              border: 'none',
              borderRadius: 10,
              fontWeight: 700,
              fontSize: '0.9rem',
              fontFamily: 'inherit',
              opacity: loading || (passwordValid && passwordsMatch) ? 1 : 0.5,
              cursor: loading || !(passwordValid && passwordsMatch) ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Please wait...' : 'Reset Password'}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: 16, fontSize: '0.85rem', color: C.muted }}>
          Back to <Link to="/login" style={{ color: C.gold, fontWeight: 700, textDecoration: 'none' }}>Sign In</Link>
        </p>
      </div>
    </div>
  );
}
